import { Modal } from "../UI/Modal/Modal.tsx";
import { useState } from "react";
import { PrimaryButton } from "../UI/PrimaryButton.tsx";
import { useCreateUserPlatformServiceAxiosInstance } from "../UserPlatformService/useCreateUserPlatformServiceAxiosInstance.ts";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { XMarkIcon } from "@heroicons/react/24/outline";

dayjs.extend(utc);
dayjs.extend(timezone);

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

export function ExportCsvModal({ isOpen, onClose }: Props) {
  const [fromDate, setFromDate] = useState<string>("");
  const [toDate, setToDate] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const { createUserPlatformServiceAxiosInstance } =
    useCreateUserPlatformServiceAxiosInstance();

  const convertToUTCStartOfDay = (dateString: string) => {
    if (!dateString) return undefined;
    return dayjs
      .tz(dateString, "Europe/Rome")
      .startOf("day")
      .utc()
      .toISOString();
  };

  const convertToUTCEndOfDay = (dateString: string) => {
    if (!dateString) return undefined;
    return dayjs.tz(dateString, "Europe/Rome").endOf("day").utc().toISOString();
  };

  const handleExport = async () => {
    try {
      setLoading(true);
      const instance = createUserPlatformServiceAxiosInstance();

      const params: Record<string, string> = {};
      if (fromDate) {
        params.fromDate = convertToUTCStartOfDay(fromDate)!;
      }
      if (toDate) {
        params.toDate = convertToUTCEndOfDay(toDate)!;
      }

      const response = await instance.get("/referral/extract-gift-card-csv", {
        params,
        responseType: "blob",
      });

      const blob = new Blob([response.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `issued_gift_cards_${Date.now()}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      onClose();
    } catch (error) {
      console.error("Error exporting CSV:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={"Export Gift Cards to CSV"}>
      <div className={"space-y-4"}>
        <div>
          <label className={"block text-sm font-medium mb-2"}>From Date</label>
          <div className={"flex items-center space-x-2"}>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className={"flex-1 px-3 py-2 border border-gray-300 rounded-md"}
            />
            {fromDate && (
              <button
                onClick={() => setFromDate("")}
                className={"p-2 hover:bg-gray-100 rounded"}
                title="Clear date"
              >
                <XMarkIcon className={"h-5 w-5 text-gray-500"} />
              </button>
            )}
          </div>
        </div>

        <div>
          <label className={"block text-sm font-medium mb-2"}>To Date</label>
          <div className={"flex items-center space-x-2"}>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className={"flex-1 px-3 py-2 border border-gray-300 rounded-md"}
            />
            {toDate && (
              <button
                onClick={() => setToDate("")}
                className={"p-2 hover:bg-gray-100 rounded"}
                title="Clear date"
              >
                <XMarkIcon className={"h-5 w-5 text-gray-500"} />
              </button>
            )}
          </div>
        </div>

        <div className={"flex justify-center mt-6"}>
          <PrimaryButton
            loading={loading}
            label={"Export CSV"}
            onClick={handleExport}
          />
        </div>
      </div>
    </Modal>
  );
}
